import {DepositDirectoryOption} from '../../models/cash-order-report';

type DirectoryState = {
    items: DepositDirectoryOption[];
    status: 'idle' | 'loading' | 'success' | 'error';
};

type ReportState = {
    selection: {
        afterDate: string;
        beforeDate: string;
        distributorId?: number;
        selectedClient: DepositDirectoryOption | null;
        selectedBranch: DepositDirectoryOption | null;
        direction: 'ASC' | 'DESC';
    };
    clients: DirectoryState;
    branches: DirectoryState;
    directoryRetry: number;
    exporting: boolean;
    actionError: boolean;
};

type ReportAction =
    | {type: 'dateChanged'; field: 'afterDate' | 'beforeDate'; value: string}
    | {type: 'distributorChanged'; value?: number}
    | {type: 'clientChanged'; value: DepositDirectoryOption | null}
    | {type: 'branchChanged'; value: DepositDirectoryOption | null}
    | {type: 'directionChanged'; value: 'ASC' | 'DESC'}
    | {type: 'directoryStarted'; directory: 'clients' | 'branches'}
    | {type: 'directoryLoaded'; directory: 'clients' | 'branches'; items: DepositDirectoryOption[]}
    | {type: 'directoryFailed'; directory: 'clients' | 'branches'}
    | {type: 'directoriesRetried'}
    | {type: 'exportStarted'}
    | {type: 'exportFinished'}
    | {type: 'actionFailed'};

const emptyDirectory = (status: DirectoryState['status'] = 'idle'): DirectoryState => ({items: [], status});

export const createDepositReportState = (today: string): ReportState => ({
    selection: {
        afterDate: today.slice(0, 7) + '-01',
        beforeDate: today,
        selectedClient: null,
        selectedBranch: null,
        direction: 'ASC',
    },
    clients: emptyDirectory(),
    branches: emptyDirectory(),
    directoryRetry: 0,
    exporting: false,
    actionError: false,
});

export const depositReportReducer = (state: ReportState, action: ReportAction): ReportState => {
    switch (action.type) {
        case 'dateChanged':
            return {...state, selection: {...state.selection, [action.field]: action.value}};
        case 'distributorChanged':
            if (state.selection.distributorId === action.value) return state;
            return {
                ...state,
                selection: {...state.selection, distributorId: action.value, selectedClient: null, selectedBranch: null},
                clients: emptyDirectory('loading'),
                branches: emptyDirectory(),
            };
        case 'clientChanged':
            if (state.selection.selectedClient?.id === action.value?.id) return state;
            return {
                ...state,
                selection: {...state.selection, selectedClient: action.value, selectedBranch: null},
                branches: emptyDirectory(action.value ? 'loading' : 'idle'),
            };
        case 'branchChanged':
            return {...state, selection: {...state.selection, selectedBranch: action.value}};
        case 'directionChanged':
            return {...state, selection: {...state.selection, direction: action.value}};
        case 'directoryStarted':
            return {...state, [action.directory]: emptyDirectory('loading')};
        case 'directoryLoaded':
            return {...state, [action.directory]: {items: action.items, status: 'success'}};
        case 'directoryFailed':
            return {...state, [action.directory]: emptyDirectory('error')};
        case 'directoriesRetried':
            return {...state, directoryRetry: state.directoryRetry + 1};
        case 'exportStarted':
            return {...state, exporting: true, actionError: false};
        case 'exportFinished':
            return {...state, exporting: false};
        case 'actionFailed':
            return {...state, actionError: true};
    }
};
